import {
  createGlobalState, useAsyncState,
  useEventBus, useIntervalFn
} from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import {
  acceptMeeting, cancelMeeting, createMeeting, getCalendar, getMeetingDetail, markMeetingRead, searchMeetings
} from '@/services/MeetingSchedulerService';
import {
  AppointmentDay,
  CalendarResponse,
  PayloadAcceptMeeting,
  PayloadCancelMeeting,
  PayloadCreateMeeting,
  AppointmentEntry,
  InvitationOverview,
  MeetingDetail, ConfirmActionEventType
} from '@/models/MeetingScheduler';
import useNotificationState from '@/state/notificationState';
import store from '@/store';
import dayjs from 'dayjs';

type MeetingSchedulerMode = 'DRAFT' | 'VIEW' | 'SEARCH';

const useMeetingScheduler = createGlobalState(() => {
  // ------------------- State -------------------
  const isOpen = ref(false);
  const mode = ref<MeetingSchedulerMode>('VIEW');
  // true when DRAFT was opened standalone (e.g. from messenger/offers) → the header X closes the
  // scheduler; false when DRAFT was entered from the already-open calendar → X goes back to VIEW.
  const isStandaloneDraft = ref(false);
  // true when the DRAFT was opened with a fixed recipient → the member search is hidden and the recipient is locked.
  const isParticipantPreset = ref(false);
  const selectedDate = ref<Date>(new Date());
  const events = useEventBus<ConfirmActionEventType>('meeting-scheduler');

  const createEmptyForm = (): PayloadCreateMeeting => ({
    participantId: '',
    timeSlots: [],
    subject: '',
    location: '',
    deadlineInDays: 7
  });

  const selectedItem = ref<AppointmentEntry | InvitationOverview | MeetingDetail | null>(null);
  const form = ref<PayloadCreateMeeting>(createEmptyForm());

  // ------------------- API requests -------------------
  function errorHandlingApiRequests(error: any) {
    const translatedError = new TranslatedBackendErrors(error.response);
    translatedError.outputGlobalErrorMessage();
  }

  const asyncOptions = {
    immediate: false,
    delay: 0,
    resetOnExecute: false,
    onError: (err: unknown) => errorHandlingApiRequests(err),
    throwError: true
  };
  const emptyCalendar: CalendarResponse = { pendingInvitations: [], appointments: [] };

  const {
    state: calendarData, execute: executeCalendar, isLoading: isCalendarLoading
  } = useAsyncState((month: string) => getCalendar(month), emptyCalendar, asyncOptions);
  const {
    state: meetingDetail, execute: executeMeetingDetail, isLoading: isMeetingDetailLoading
  } = useAsyncState((meetingId: string) => getMeetingDetail(meetingId), null, asyncOptions);
  const {
    execute: executeCreateMeeting, isLoading: isCreateMeetingLoading
  } = useAsyncState((payload: PayloadCreateMeeting) => createMeeting(payload), null, asyncOptions);
  const { execute: executeAcceptMeeting } = useAsyncState((meetingId: string, payload: PayloadAcceptMeeting) => acceptMeeting(meetingId, payload), null, asyncOptions);
  const { execute: executeCancelMeeting } = useAsyncState((meetingId: string, payload?: PayloadCancelMeeting) => cancelMeeting(meetingId, payload), null, asyncOptions);
  const {
    state: searchResults, execute: executeSearchMeetings, isLoading: isSearchLoading
  } = useAsyncState((q: string) => searchMeetings(q), null, asyncOptions);

  // Thin wrappers so callers don't have to deal with the useAsyncState `execute(delay, ...args)` signature.
  const fetchMeetingDetail = (meetingId: string) => executeMeetingDetail(0, meetingId);
  const fetchCreateMeeting = (payload: PayloadCreateMeeting) => executeCreateMeeting(0, payload);
  const fetchAcceptMeeting = (meetingId: string, payload: PayloadAcceptMeeting) => executeAcceptMeeting(0, meetingId, payload);
  const fetchCancelMeeting = (meetingId: string, payload?: PayloadCancelMeeting) => executeCancelMeeting(0, meetingId, payload);
  const fetchSearchMeetings = (q: string) => executeSearchMeetings(0, q);

  // ------------------- Derived / helpers -------------------
  const currentCalendarMonth = ref(dayjs().format('YYYY-MM'));
  const refreshCalendar = (date: Date = selectedDate.value) => {
    currentCalendarMonth.value = dayjs(date ?? new Date()).format('YYYY-MM');
    return executeCalendar(0, currentCalendarMonth.value);
  };

  // The DRAFT conflict check also needs months the picker isn't showing; calendarData only holds one.
  const appointmentsByMonth = ref<Record<string, AppointmentEntry[]>>({});
  watch(calendarData, (data) => {
    appointmentsByMonth.value = {
      ...appointmentsByMonth.value,
      [currentCalendarMonth.value]: data.appointments.flatMap((day) => day.meetings)
    };
  });

  function resetState() {
    selectedItem.value = null;
    meetingDetail.value = null;
    form.value = createEmptyForm();
    isParticipantPreset.value = false;
  }

  const syncOpenMeeting = async () => {
    const meetingId = selectedItem.value?.meetingId;
    if (mode.value !== 'VIEW' || !meetingId) return;
    try {
      const detail = await getMeetingDetail(meetingId);
      if (detail.status === 'CANCELLED' || detail.status === 'EXPIRED') {
        resetState();
      } else if (detail.status !== meetingDetail.value?.status) {
        meetingDetail.value = detail;
      }
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) resetState();
    }
  };

  const silentRefreshCalendar = async () => {
    try {
      calendarData.value = await getCalendar(currentCalendarMonth.value);
      await syncOpenMeeting();
    } catch {
      // keep the current data on transient polling errors
    }
  };
  const { pause: pauseCalendarPolling, resume: resumeCalendarPolling } = useIntervalFn(silentRefreshCalendar, 60000, { immediate: false });

  const selectedAppointments = computed<AppointmentDay | null>(() => {
    if (!selectedDate.value) return null;
    const dayString = dayjs(selectedDate.value).format('YYYY-MM-DD');
    return calendarData.value.appointments.find((appointment) => appointment.day === dayString) ?? null;
  });

  // Date picker "years" overview range: current year + next YEARS_AHEAD years (display-only, doesn't clamp selectable dates).
  const pickerYearRange = computed(() => {
    const YEARS_AHEAD = 15;
    const currentYear = dayjs().year();
    return { earliestYear: currentYear, latestYear: currentYear + YEARS_AHEAD };
  });

  // Precomputed set of days that have appointments → O(1) lookup for the date picker's per-cell dot.
  const appointmentDays = computed(() => new Set(calendarData.value.appointments.map((day) => day.day)));
  const hasAppointment = (date: Date): boolean => appointmentDays.value.has(dayjs(date).format('YYYY-MM-DD'));

  // ------------------- Mode transitions -------------------
  // Every mode/open switch goes through one of these, so a new entry point can't half-forget a field.

  // Draft started from inside the already open scheduler.
  function startDraft(participantId = '', standalone = false) {
    resetState();
    form.value.participantId = participantId;
    isParticipantPreset.value = !!participantId;
    isStandaloneDraft.value = standalone;
    mode.value = 'DRAFT';
  }

  // Draft started from outside (messenger, offers) — also opens the popup.
  function openCreate(participantId = '') {
    startDraft(participantId, true);
    selectedDate.value = new Date();
    isOpen.value = true;
    refreshCalendar();
  }

  function showMeeting(item: AppointmentEntry | InvitationOverview | MeetingDetail | null, date?: Date) {
    resetState();
    if (date) selectedDate.value = date;
    selectedItem.value = item;
    mode.value = 'VIEW';
  }

  function openSearch() {
    resetState();
    searchResults.value = null;
    mode.value = 'SEARCH';
  }

  function backToCalendar() {
    resetState();
    mode.value = 'VIEW';
  }

  function openCalendar() {
    backToCalendar();
    selectedDate.value = new Date();
    if (isOpen.value) refreshCalendar();
    else isOpen.value = true;
  }

  function closeScheduler() {
    isOpen.value = false;
    backToCalendar();
    selectedDate.value = new Date();
  }

  const markInvitationRead = async (detail: MeetingDetail | null) => {
    const currentUserId: string | undefined = store.getters.user?.userId;
    if (!detail || detail.status !== 'PENDING' || detail.participant.userId !== currentUserId) return;
    await markMeetingRead(detail.meetingId);
    await useNotificationState().loadNotifications();
  };

  watch(selectedItem, async (meeting) => {
    if (meeting?.meetingId) {
      const detail = await fetchMeetingDetail(meeting.meetingId);
      await markInvitationRead(detail);
    } else meetingDetail.value = null;
  });

  watch(isOpen, (open) => {
    if (open) resumeCalendarPolling();
    else pauseCalendarPolling();
  });

  return {
    isOpen,
    mode,
    isStandaloneDraft,
    isParticipantPreset,
    selectedDate,
    events,
    selectedItem,
    form,
    refreshCalendar,
    fetchMeetingDetail,
    fetchCreateMeeting,
    calendarData,
    appointmentsByMonth,
    selectedAppointments,
    openCreate,
    openCalendar,
    startDraft,
    showMeeting,
    openSearch,
    backToCalendar,
    closeScheduler,
    meetingDetail,
    fetchAcceptMeeting,
    fetchCancelMeeting,
    searchResults,
    fetchSearchMeetings,
    resetState,
    hasAppointment,
    pickerYearRange,
    isCalendarLoading,
    isMeetingDetailLoading,
    isCreateMeetingLoading,
    isSearchLoading
  };
});

export default useMeetingScheduler;
